import { parseTermId } from '../../../utils/termIdParser';
import type { GridPaymentGridData, GridWrittenHalves } from '../../../types/paymentTypes';
import type { GenericObject, Offer, OfferType } from '@makemydeal/dr-platform-types';
import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { PartialPenciledInputsWithAllTypes, PenciledInputsState } from '../../../types/commonTypes';
import * as types from '../../../types/actionTypes';
import { updateInputWithValue, updateOfferTypeWithInputs } from '../helpers';
import { updatePaymentGridLoadingStatuses } from '../../../utils/paymentGridUtils';
import { appliesToTerm, resolveDealColumnIndex, resolveTermOwnerColumnIndex } from '../../../utils/paymentGridSourceUtils';
import { GRID_RATE_KEYS, GRID_RESIDUAL_KEYS, overridesTypedInGrid } from '../../../utils/gridWrittenOverrides';
import { GRID_TYPED_OVERRIDES, RATE_TYPE } from '../../../constants/payment';
import { isRateInAnotherUnit } from '../../../utils/rateUtils';

type ColumnCells = Record<number, Record<number, Record<string, unknown>>>;

type GenericColumnOverride = {
    sellRate?: number;
    rateType?: string;
    residualInfo?: Record<string, unknown>;
    term?: number | null;
};

const isColumnRateType = (rateType: unknown): rateType is string =>
    rateType === RATE_TYPE.APR || rateType === RATE_TYPE.MoneyFactor;

const projectVerdictOntoColumns = (
    terms: (number | null)[] | undefined,
    verdictByTerm: Record<number, string>
): Record<number, string> =>
    (terms || []).reduce<Record<number, string>>((byColumn, term, columnIndex) => {
        if (term != null && verdictByTerm[term as number] !== undefined) byColumn[columnIndex] = verdictByTerm[term as number];
        return byColumn;
    }, {});

const isHeldByMoreThanOneColumn = (terms: (number | null)[], term: number | null): boolean =>
    terms.filter((gridTerm) => gridTerm === term).length > 1;

const dealColumnIndexOf = (currentOffer: any): number | undefined =>
    resolveTermOwnerColumnIndex(
        (currentOffer?.paymentGridData as GridPaymentGridData)?.terms,
        (currentOffer?.paymentGridData as GridPaymentGridData)?.activeCell?.columnIndex,
        parseTermId(currentOffer?.term)?.termIdParams?.term
    );

const nextErroredColumns = (previous: number[] | undefined, columnIndices: number[] | undefined, hasFailed: boolean): number[] => {
    if (!columnIndices?.length) return previous ?? [];
    return hasFailed
        ? [...new Set([...(previous ?? []), ...columnIndices])]
        : (previous ?? []).filter((columnIndex) => !columnIndices.includes(columnIndex));
};

const collapseColumnOverridesIntoTerms = (
    overridesByTerm: Record<number, any> | undefined,
    terms: (number | null)[] | undefined,
    columnOverrides: Record<number, GenericColumnOverride> | undefined,
    selectedColumnIndex: number | undefined,
    gridWrittenTerms: Record<number, { rate?: true; residual?: true }> | undefined
): Record<number, any> | undefined => {
    if (!columnOverrides || !Object.keys(columnOverrides).length || !terms?.length) return undefined;

    const next: Record<number, any> = { ...(overridesByTerm || {}) };
    let changed = false;

    [...new Set(terms.filter((term): term is number => term != null))].forEach((term) => {
        const positions = terms.reduce<number[]>((held, gridTerm, columnIndex) => {
            if (gridTerm === term) held.push(columnIndex);
            return held;
        }, []);

        const typedPositions = positions.filter((columnIndex) => {
            const entry = columnOverrides[columnIndex];
            return appliesToTerm(entry, term) && (entry.sellRate != null || entry.residualInfo != null);
        });
        if (!typedPositions.length) return;

        const chosen = selectedColumnIndex != null && positions.includes(selectedColumnIndex) ? selectedColumnIndex : positions[0];
        const own = columnOverrides[chosen];
        const ownApplies = appliesToTerm(own, term);

        const gridWroteRate = gridWrittenTerms?.[term]?.rate === true;
        const gridWroteResidual = gridWrittenTerms?.[term]?.residual === true;
        const carried = {
            ...next[term],
            ...(ownApplies && own.sellRate != null
                ? { sellRate: own.sellRate, ...(own.rateType ? { rateType: own.rateType } : {}) }
                : gridWroteRate
                ? { sellRate: undefined, rateType: undefined }
                : {}),
            ...(ownApplies && own.residualInfo
                ? { residualInfo: own.residualInfo }
                : gridWroteResidual
                ? { residualInfo: undefined }
                : {})
        };
        next[term] = carried;
        changed = true;
    });

    return changed ? next : undefined;
};

const remapByColumnIndex = <T>(previous: Record<number, T>, sortedOrder: number[], dropColumnIndex?: number): Record<number, T> =>
    sortedOrder.reduce<Record<number, T>>((next, oldIndex, newIndex) => {
        const value = previous[oldIndex];
        if (value !== undefined && oldIndex !== dropColumnIndex) next[newIndex] = value;
        return next;
    }, {});

const withoutHalvesWritten = <T extends { term: number; sellRate?: unknown; residual?: unknown }>(
    byColumn: Record<number, T>,
    columnIndex: number,
    override: GenericObject
): Record<number, T> => {
    const entry = byColumn[columnIndex];
    if (!entry) return byColumn;
    const remaining = { ...entry };
    if ('sellRate' in override) delete remaining.sellRate;
    if ('residualInfo' in override) delete remaining.residual;
    const { [columnIndex]: _written, ...others } = byColumn;
    return remaining.sellRate === undefined && remaining.residual === undefined ? others : { ...others, [columnIndex]: remaining };
};

const dropColumnCells = (columnCells: ColumnCells, columnIndex: number): ColumnCells =>
    Object.fromEntries(Object.entries(columnCells).filter(([column]) => Number(column) !== columnIndex));

const remapColumnCells = (columnCells: ColumnCells, sortedOrder: number[]): ColumnCells =>
    sortedOrder.reduce<ColumnCells>((next, oldIndex, newIndex) => {
        const rows = columnCells[oldIndex];
        if (rows) next[newIndex] = rows;
        return next;
    }, {});

const remapColumnCellRows = (columnCells: ColumnCells, sortedRowOrder: number[]): ColumnCells =>
    Object.entries(columnCells).reduce<ColumnCells>((next, [column, rows]) => {
        const remappedRows = sortedRowOrder.reduce<Record<number, Record<string, unknown>>>((byRow, oldRow, newRow) => {
            if (rows?.[oldRow] !== undefined) byRow[newRow] = rows[oldRow];
            return byRow;
        }, {});
        next[Number(column)] = remappedRows;
        return next;
    }, {});

const mergeColumnCells = (previous: ColumnCells | undefined, incoming: ColumnCells): ColumnCells =>
    Object.entries(incoming).reduce<ColumnCells>(
        (next, [column, rows]) => ({ ...next, [Number(column)]: { ...next[Number(column)], ...rows } }),
        { ...(previous || {}) }
    );

const withoutGridWrittenOverrides = (
    overridesByTerm: Record<number, any>,
    gridWrittenTerms: Record<number, { rate?: true; residual?: true }>
): Record<number, any> =>
    Object.keys(gridWrittenTerms).reduce((accumulated: Record<number, any>, key) => {
        const termMonths = Number(key);
        const previous = accumulated[termMonths];
        if (!previous) return accumulated;

        const halves = gridWrittenTerms[termMonths];
        const keptTermOverrides: Record<string, any> = { ...previous };
        if (halves.rate) GRID_RATE_KEYS.forEach((rateKey) => delete keptTermOverrides[rateKey]);

        if (halves.residual && previous.residualInfo) {
            const keptResidualInfo: Record<string, any> = { ...previous.residualInfo };
            GRID_RESIDUAL_KEYS.forEach((residualKey) => delete keptResidualInfo[residualKey]);
            if (Object.keys(keptResidualInfo).length) {
                keptTermOverrides.residualInfo = keptResidualInfo;
            } else {
                delete keptTermOverrides.residualInfo;
            }
        }

        if (!Object.keys(keptTermOverrides).length) {
            const { [termMonths]: _removedTerm, ...remaining } = accumulated;
            return remaining;
        }
        return { ...accumulated, [termMonths]: keptTermOverrides };
    }, overridesByTerm);

export const handlePaymentGridCases = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    //unused variable
    _offer: Offer,
    isUserProgramQuotes: boolean,
    isPaymentGridRedesignEnabled = false
): PartialPenciledInputsWithAllTypes => {
    switch (action.type) {
        case types.UPDATED_PAYMENT_GRID_TOGGLE: {
            const updatedStatus = action.payload;
            const prevPaymentGridData = state.byOfferType?.[offerType]?.paymentGridData || {};

            // Stash the MV-priced grid cells that ride on the enabling action so Sales View can render the
            // non-active cells straight from the baggage. On SV only the active term is hydrated into
            // terms.byId; the rest were priced in MV solely for the grid. Indexed [downPaymentIndex][termIndex]
            // to match the grid axes (cashDown x terms). Cleared when the grid is turned off.
            const gridCellPayments = updatedStatus ? action.meta?.preInitializedPaymentGrid?.cells : undefined;
            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'paymentGridData', {
                ...prevPaymentGridData,
                paymentGridToggle: updatedStatus,
                ...((prevPaymentGridData as GridPaymentGridData).awaitingVehicleSwitch && { awaitingVehicleSwitch: undefined }),
                ...(gridCellPayments && { gridCellPayments })
            });

            if (updatedStatus && offerType === LEASE) {
                inputs.isTargetOutOfPocket = true;
            }

            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.INITIALIZE_PAYMENT_GRID_FROM_TEMPLATE: {
            const actionOfferType = (action.payload?.offerType || FINANCE) as OfferType;
            const byOfferType = state.byOfferType as Record<OfferType, any> | undefined;
            const prevPaymentGridData = byOfferType?.[actionOfferType]?.paymentGridData || {};

            const inputs = updateInputWithValue(byOfferType?.[actionOfferType], 'paymentGridData', {
                ...prevPaymentGridData,
                paymentGridToggle: true
            });

            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, actionOfferType)
            };
        }

        case types.INITIALIZE_PAYMENT_GRID_TERMS: {
            const { offerType, terms } = action.payload;
            const currentOffer = state.byOfferType?.[offerType as keyof typeof state.byOfferType];
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            terms
                        }
                    }
                }
            };
        }

        case types.INITIALIZE_PAYMENT_GRID_CASHDOWN: {
            const { offerType, cashDown } = action.payload;
            const currentOffer = state.byOfferType?.[offerType as keyof typeof state.byOfferType];
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            cashDown
                        }
                    }
                }
            };
        }

        case types.UPDATE_PAYMENT_GRID_TERM: {
            const { offerType, key, val } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[offerType as keyof typeof state.byOfferType];
            const paymentGridData = currentOffer.paymentGridData;
            const unsortedTerms = [...(paymentGridData?.terms || [])];
            const replacedTerm = unsortedTerms[key];
            unsortedTerms[key] = val;

            // Nulls last, every other column keeping its order - sorted through an index list so the same permutation
            // applies to anything else keyed by column index, e.g. a parked sell rate.
            const sortedOrder = unsortedTerms
                .map((_term, index) => index)
                .sort((a, b) => {
                    if (unsortedTerms[a] === null && unsortedTerms[b] !== null) return 1;
                    if (unsortedTerms[a] !== null && unsortedTerms[b] === null) return -1;
                    return 0;
                });
            const newTerms = sortedOrder.map((index) => unsortedTerms[index]);

            // A parked rate follows its column through the sort. The edited column's entry is dropped when the edit gave
            // it a real term, or it would be applied again to the next term typed there; a term cleared to empty keeps it.
            const previousPendingSellRates = (paymentGridData as GridPaymentGridData)?.pendingSellRates;
            const remappedPendingSellRates =
                previousPendingSellRates &&
                sortedOrder.reduce<Record<number, number>>((rates, oldIndex, newIndex) => {
                    const parkedRate = previousPendingSellRates[oldIndex];
                    const isConvertedByThisEdit = oldIndex === key && val != null;
                    if (parkedRate != null && !isConvertedByThisEdit) {
                        rates[newIndex] = parkedRate;
                    }
                    return rates;
                }, {});

            // A term edit re-discovers and re-prices, so the edited column keeps no verdict - inheriting a sticky
            // `unavailable` would suppress the fresh attempt. Keyed by term value, so the replaced term's entry survives
            // while another column still carries it.
            const previousColumnCells = (paymentGridData as GridPaymentGridData)?.columnCells as ColumnCells | undefined;
            const previousDecidedColumns = (paymentGridData as GridPaymentGridData)?.decidedColumns as
                | Record<number, string>
                | undefined;
            const previousColumnOverrides = (paymentGridData as GridPaymentGridData)?.columnOverrides as
                | Record<number, GenericColumnOverride>
                | undefined;
            const previousActiveCell = (paymentGridData as GridPaymentGridData)?.activeCell;
            const previousErroredColumns = (paymentGridData as GridPaymentGridData)?.erroredColumns;
            const remappedActiveColumnIndex =
                previousActiveCell?.columnIndex == null ? -1 : sortedOrder.indexOf(previousActiveCell.columnIndex);
            const previousColumnSources = (paymentGridData as GridPaymentGridData)?.columnSources;
            const isTermValueChanged = replacedTerm !== val;
            const dealTermMonths = parseTermId(currentOffer?.term)?.termIdParams?.term;
            const isNewTermHeldElsewhere =
                isPaymentGridRedesignEnabled &&
                val != null &&
                val === dealTermMonths &&
                newTerms.filter((term) => term === val).length > 1;
            const termsToForget = isTermValueChanged
                ? [
                      ...(replacedTerm != null && !newTerms.includes(replacedTerm) ? [replacedTerm] : []),
                      ...(val != null && !isNewTermHeldElsewhere ? [val] : [])
                  ]
                : [];
            const remainingColumnSources =
                previousColumnSources && termsToForget.length
                    ? Object.fromEntries(
                          Object.entries(previousColumnSources).filter(([term]) => !termsToForget.includes(Number(term)))
                      )
                    : previousColumnSources;

            // A term leaving the grid also leaves every row's quote ledger, or re-typing a term this build held earlier
            // would read as already quoted. Deliberately the same `termsToForget` list as `columnSources` above.
            const previousRowsStatuses = (paymentGridData as GridPaymentGridData)?.rowsStatuses as Record<string, any> | undefined;
            const prunedRowsStatuses =
                previousRowsStatuses && termsToForget.length
                    ? Object.fromEntries(
                          Object.entries(previousRowsStatuses).map(([downPayment, rowStatus]) => [
                              downPayment,
                              Array.isArray(rowStatus?.quotedTerms)
                                  ? {
                                        ...rowStatus,
                                        quotedTerms: rowStatus.quotedTerms.filter(
                                            (termMonths: number) => !termsToForget.includes(termMonths)
                                        )
                                    }
                                  : rowStatus
                          ])
                      )
                    : previousRowsStatuses;

            const previousUserEnteredTerms = (paymentGridData as GridPaymentGridData)?.userEnteredTerms;
            const retainedUserEnteredTerms = previousUserEnteredTerms
                ? Object.fromEntries(
                      Object.entries(previousUserEnteredTerms).filter(([term]) => !termsToForget.includes(Number(term)))
                  )
                : {};
            const userEnteredTerms = val != null ? { ...retainedUserEnteredTerms, [val]: true } : retainedUserEnteredTerms;
            const editedColumnAfterSort = sortedOrder.indexOf(key);
            const previousUserEnteredColumns = (paymentGridData as GridPaymentGridData)?.userEnteredColumns ?? [];
            const userEnteredColumns = sortedOrder.reduce<number[]>((columns, oldIndex, newIndex) => {
                const isEditedColumn = newIndex === editedColumnAfterSort;
                if (isEditedColumn ? val != null : previousUserEnteredColumns.includes(oldIndex)) columns.push(newIndex);
                return columns;
            }, []);

            const previousRestoredInputs = (paymentGridData as GridPaymentGridData)?.restoredInputs;
            const previousCarriedOverrides = (paymentGridData as GridPaymentGridData)?.carriedOverrides;

            const withoutForgottenTerms = <T>(byTerm: Record<number, T> | undefined) => {
                if (!byTerm || !termsToForget.length) return byTerm;
                const remaining = Object.fromEntries(
                    Object.entries(byTerm).filter(([term]) => !termsToForget.includes(Number(term)))
                ) as Record<number, T>;
                return Object.keys(remaining).length ? remaining : undefined;
            };
            const previousOriginalColumnSources = (paymentGridData as GridPaymentGridData)?.originalColumnSources;
            const previousOriginalColumnPrograms = (paymentGridData as GridPaymentGridData)?.originalColumnPrograms;

            const previousGridWrittenTerms = (paymentGridData as GridPaymentGridData)?.gridWrittenTerms;
            const gridWrittenTerms =
                previousGridWrittenTerms && termsToForget.length
                    ? Object.fromEntries(
                          Object.entries(previousGridWrittenTerms).filter(([term]) => !termsToForget.includes(Number(term)))
                      )
                    : previousGridWrittenTerms;

            const remappedColumnOverrides = previousColumnOverrides
                ? remapByColumnIndex(previousColumnOverrides, sortedOrder, key)
                : undefined;

            const remappedDecidedColumns = previousDecidedColumns
                ? remapByColumnIndex(previousDecidedColumns, sortedOrder, key)
                : undefined;
            const forgottenNewTermVerdict =
                isPaymentGridRedesignEnabled && val != null && termsToForget.includes(val)
                    ? previousColumnSources?.[val as number]
                    : undefined;
            const decidedColumns = forgottenNewTermVerdict
                ? newTerms.reduce<Record<number, string>>(
                      (decided, term, columnIndex) => {
                          if (term === val && sortedOrder[columnIndex] !== key && decided[columnIndex] == null) {
                              decided[columnIndex] = forgottenNewTermVerdict;
                          }
                          return decided;
                      },
                      { ...remappedDecidedColumns }
                  )
                : remappedDecidedColumns;
            const orphanedHalves =
                isTermValueChanged && replacedTerm != null ? previousGridWrittenTerms?.[replacedTerm] : undefined;
            const isReplacedTermStillOwned = newTerms.some((term, columnIndex) => {
                if (term !== replacedTerm) return false;
                const own = remappedColumnOverrides?.[columnIndex];
                return own != null && (own.sellRate != null || own.residualInfo != null);
            });
            const previousOverridesByTerm: Record<number, any> = (currentOffer as any)?.overridesByTerm || {};
            const takenBackOverridesByTerm =
                orphanedHalves && !isReplacedTermStillOwned && !action.meta?.holdsLeftTerm
                    ? withoutGridWrittenOverrides(previousOverridesByTerm, { [replacedTerm as number]: orphanedHalves })
                    : undefined;
            const gridWrittenTermsAfterTakeback = takenBackOverridesByTerm
                ? // Asserted, not guarded: a take-back requires `orphanedHalves`, which requires the ledger to exist.
                  Object.fromEntries(
                      Object.entries(gridWrittenTerms as Record<number, GridWrittenHalves>).filter(
                          ([term]) => Number(term) !== replacedTerm
                      )
                  )
                : gridWrittenTerms;

            const holdersOf = (term: number) =>
                newTerms.reduce<number[]>((held, gridTerm, columnIndex) => (gridTerm === term ? [...held, columnIndex] : held), []);
            const halvesOwnedBy = (term: number, columnIndices: number[]) =>
                columnIndices.reduce<GridWrittenHalves>((owned, columnIndex) => {
                    const own = remappedColumnOverrides?.[columnIndex];
                    if (!appliesToTerm(own, term)) return owned;
                    return {
                        ...owned,
                        ...(own.sellRate != null && { rate: true as const }),
                        ...(own.residualInfo != null && { residual: true as const })
                    };
                }, {});

            const joinedTermHalves =
                isPaymentGridRedesignEnabled && isTermValueChanged && val != null && val !== dealTermMonths
                    ? halvesOwnedBy(
                          val,
                          holdersOf(val).filter((columnIndex) => sortedOrder[columnIndex] !== key)
                      )
                    : {};

            const replacedTermHolders =
                isPaymentGridRedesignEnabled && isTermValueChanged && replacedTerm != null ? holdersOf(replacedTerm) : [];
            const previousSplitOffTerms = (paymentGridData as GridPaymentGridData)?.splitOffTerms;
            const retainedSplitOffTerms = withoutForgottenTerms(previousSplitOffTerms);
            const splitOffTerms =
                replacedTermHolders.length === 1
                    ? { ...retainedSplitOffTerms, [replacedTerm as number]: true as const }
                    : retainedSplitOffTerms;
            const dealColumnBeforeEdit = resolveDealColumnIndex(
                paymentGridData?.terms,
                previousActiveCell?.columnIndex,
                dealTermMonths
            );
            const isSurvivorTheDeals = replacedTerm === dealTermMonths && key !== dealColumnBeforeEdit;
            const survivor = replacedTermHolders.length === 1 && !isSurvivorTheDeals ? replacedTermHolders[0] : undefined;
            const survivorOverride =
                survivor != null && appliesToTerm(remappedColumnOverrides?.[survivor], replacedTerm as number)
                    ? remappedColumnOverrides?.[survivor]
                    : undefined;
            const survivorHalves = survivor != null ? halvesOwnedBy(replacedTerm as number, [survivor]) : {};

            const loneHolderVerdict =
                replacedTermHolders.length === 1 && remainingColumnSources?.[replacedTerm as number] == null
                    ? decidedColumns?.[replacedTermHolders[0]]
                    : undefined;
            const nextColumnSources = loneHolderVerdict
                ? { ...remainingColumnSources, [replacedTerm as number]: loneHolderVerdict }
                : remainingColumnSources;
            const loneHolderCells =
                replacedTermHolders.length === 1 && previousColumnCells
                    ? remapColumnCells(dropColumnCells(previousColumnCells, key), sortedOrder)[replacedTermHolders[0]]
                    : undefined;
            const nextRowsStatuses =
                loneHolderCells && prunedRowsStatuses
                    ? Object.fromEntries(
                          Object.entries(prunedRowsStatuses).map(([rowKey, rowStatus]) => {
                              const cell = loneHolderCells[Number(rowKey)];
                              const isPricedHere =
                                  cell?.term === replacedTerm &&
                                  cell?.monthlyPayment != null &&
                                  Array.isArray(rowStatus?.quotedTerms) &&
                                  !rowStatus.quotedTerms.includes(replacedTerm);
                              return [
                                  rowKey,
                                  isPricedHere ? { ...rowStatus, quotedTerms: [...rowStatus.quotedTerms, replacedTerm] } : rowStatus
                              ];
                          })
                      )
                    : prunedRowsStatuses;

            let nextOverridesByTerm = takenBackOverridesByTerm ?? previousOverridesByTerm;
            let nextGridWrittenTerms = gridWrittenTermsAfterTakeback;
            if (Object.keys(joinedTermHalves).length) {
                nextOverridesByTerm = withoutGridWrittenOverrides(nextOverridesByTerm, { [val as number]: joinedTermHalves });
            }
            if (survivorOverride && Object.keys(survivorHalves).length) {
                const cleared = withoutGridWrittenOverrides(nextOverridesByTerm, {
                    [replacedTerm as number]: orphanedHalves ?? {}
                })[replacedTerm as number];
                nextOverridesByTerm = {
                    ...nextOverridesByTerm,
                    [replacedTerm as number]: {
                        ...cleared,
                        ...(survivorOverride.sellRate != null && {
                            sellRate: survivorOverride.sellRate,
                            ...(survivorOverride.rateType != null && { rateType: survivorOverride.rateType })
                        }),
                        ...(survivorOverride.residualInfo != null && {
                            residualInfo: { ...cleared?.residualInfo, ...survivorOverride.residualInfo }
                        })
                    }
                };
                nextGridWrittenTerms = { ...nextGridWrittenTerms, [replacedTerm as number]: survivorHalves };
            }

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...currentOffer,
                        ...(nextOverridesByTerm !== previousOverridesByTerm && { overridesByTerm: nextOverridesByTerm }),
                        paymentGridData: {
                            ...paymentGridData,
                            lastUpdatedInput: `input_term_${key}`,
                            terms: newTerms,
                            userEnteredTerms,
                            ...(isPaymentGridRedesignEnabled && { userEnteredColumns }),
                            ...((previousSplitOffTerms || splitOffTerms) && { splitOffTerms }),
                            gridWrittenTerms: nextGridWrittenTerms,
                            ...(previousRowsStatuses && { rowsStatuses: nextRowsStatuses }),
                            ...(previousColumnSources && { columnSources: nextColumnSources }),
                            ...(previousOriginalColumnSources && {
                                originalColumnSources: withoutForgottenTerms(previousOriginalColumnSources)
                            }),
                            ...(previousOriginalColumnPrograms && {
                                originalColumnPrograms: withoutForgottenTerms(previousOriginalColumnPrograms)
                            }),
                            ...(previousPendingSellRates && { pendingSellRates: remappedPendingSellRates }),
                            ...(previousColumnCells && {
                                columnCells: remapColumnCells(dropColumnCells(previousColumnCells, key), sortedOrder)
                            }),
                            ...(decidedColumns && { decidedColumns }),
                            ...(previousColumnOverrides && {
                                columnOverrides: remapByColumnIndex(previousColumnOverrides, sortedOrder, key)
                            }),
                            ...(previousRestoredInputs && {
                                restoredInputs: remapByColumnIndex(previousRestoredInputs, sortedOrder, key)
                            }),
                            ...(previousCarriedOverrides && {
                                carriedOverrides: remapByColumnIndex(previousCarriedOverrides, sortedOrder, key)
                            }),
                            /**
                             * A null axis value is not stamped. Nulls sort last, so an edit that CLEARS the active
                             * column follows the sort into the empty slot - and every reader validates the stamp with
                             * `terms[i] === stamp.term`, which passes on `null === null` and resolves the active cell
                             * to a column holding nothing. Clearing the half instead lets resolution fall through to
                             * the deal, which is what an active column without a term means.
                             */
                            ...(previousActiveCell?.columnIndex != null && {
                                activeCell: {
                                    ...previousActiveCell,
                                    columnIndex:
                                        newTerms[remappedActiveColumnIndex] == null ? undefined : remappedActiveColumnIndex,
                                    term: newTerms[remappedActiveColumnIndex] ?? undefined
                                }
                            }),
                            ...(previousErroredColumns && {
                                erroredColumns: previousErroredColumns
                                    .filter((columnIndex) => columnIndex !== key)
                                    .map((columnIndex) => sortedOrder.indexOf(columnIndex))
                                    .filter((columnIndex) => columnIndex >= 0)
                            })
                        },
                        buyRateOverride: undefined
                    }
                }
            };
        }

        case types.UPDATE_PAYMENT_GRID_CASH_DOWN: {
            const { offerType, key, val } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[offerType as keyof typeof state.byOfferType];
            const paymentGridData = currentOffer.paymentGridData;

            if (!paymentGridData?.cashDown || !Array.isArray(paymentGridData.cashDown)) {
                return state;
            }
            const unsortedCashDown = [...paymentGridData.cashDown];
            unsortedCashDown[key] = val;

            const sortedRowOrder = unsortedCashDown
                .map((_cash, rowIndex) => rowIndex)
                .sort((a, b) => {
                    if (unsortedCashDown[a] === null && unsortedCashDown[b] !== null) return 1;
                    if (unsortedCashDown[a] !== null && unsortedCashDown[b] === null) return -1;
                    return 0;
                });
            const newCashDown = sortedRowOrder.map((rowIndex) => unsortedCashDown[rowIndex]);
            const editedRowIndex = sortedRowOrder.indexOf(key);

            const previousActiveCell = (paymentGridData as GridPaymentGridData)?.activeCell;
            const remappedActiveRowIndex =
                previousActiveCell?.rowIndex == null ? -1 : sortedRowOrder.indexOf(previousActiveCell.rowIndex);
            const previousColumnCells = (paymentGridData as GridPaymentGridData)?.columnCells as ColumnCells | undefined;
            const previousRowsStatuses = (paymentGridData as GridPaymentGridData)?.rowsStatuses as Record<number, any> | undefined;
            const remappedRowsStatuses = previousRowsStatuses
                ? remapByColumnIndex(previousRowsStatuses, sortedRowOrder)
                : previousRowsStatuses;
            const rowsStatusesForNewKey =
                remappedRowsStatuses && val != null
                    ? { ...remappedRowsStatuses, [editedRowIndex]: { ...remappedRowsStatuses[editedRowIndex], quotedTerms: [] } }
                    : remappedRowsStatuses;

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...paymentGridData,
                            lastUpdatedInput: `input_cash_${key}`,
                            cashDown: newCashDown,
                            ...(previousActiveCell?.rowIndex != null && {
                                activeCell: {
                                    ...previousActiveCell,
                                    rowIndex: newCashDown[remappedActiveRowIndex] == null ? undefined : remappedActiveRowIndex,
                                    downPayment: newCashDown[remappedActiveRowIndex] ?? undefined
                                }
                            }),
                            ...(previousColumnCells && { columnCells: remapColumnCellRows(previousColumnCells, sortedRowOrder) }),
                            ...(previousRowsStatuses && { rowsStatuses: rowsStatusesForNewKey })
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_COLUMN_SOURCES: {
            const {
                offerType: actionOfferType,
                columnSources,
                replace,
                refusedTerms,
                pairRejectedTerms,
                keepColumnVerdictsFor
            } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const currentGridData = currentOffer?.paymentGridData as GridPaymentGridData;

            const mergeTermFlags = (flags?: Record<number, true>, previousFlags?: Record<number, true>) =>
                flags &&
                (replace
                    ? { ...flags }
                    : Object.keys(columnSources).reduce(
                          (accumulated: Record<number, true>, key) => {
                              const termMonths = Number(key);
                              if (flags[termMonths]) return { ...accumulated, [termMonths]: true as const };
                              const { [termMonths]: _noLongerFlagged, ...remaining } = accumulated;
                              return remaining;
                          },
                          { ...previousFlags }
                      ));
            const nextRefusedTerms = mergeTermFlags(refusedTerms, currentGridData?.refusedTerms);
            const nextPairRejectedTerms = mergeTermFlags(pairRejectedTerms, currentGridData?.pairRejectedTerms);

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            // Merged by default, so a per-column recalculation revises one column without clearing the
                            // others. `replace` is for the whole-grid resolution pass, the single writer of every
                            // column's verdict for its build: merging there let an `Unavailable` from a superseded build
                            // survive, and only `RESET_PAYMENT_GRID` removes an entry.
                            columnSources: replace
                                ? { ...columnSources }
                                : {
                                      ...(currentOffer?.paymentGridData as GridPaymentGridData)?.columnSources,
                                      ...columnSources
                                  },
                            decidedColumns: (() => {
                                const projected = projectVerdictOntoColumns(
                                    (currentOffer?.paymentGridData as GridPaymentGridData)?.terms,
                                    isPaymentGridRedesignEnabled && keepColumnVerdictsFor?.length
                                        ? Object.fromEntries(
                                              Object.entries(columnSources as Record<number, string>).filter(
                                                  ([termMonths]) => !keepColumnVerdictsFor.includes(Number(termMonths))
                                              )
                                          )
                                        : columnSources
                                );
                                if (replace) return projected;
                                if (!isPaymentGridRedesignEnabled) {
                                    return {
                                        ...(currentOffer?.paymentGridData as GridPaymentGridData)?.decidedColumns,
                                        ...projected
                                    };
                                }
                                const gridTerms = (currentOffer?.paymentGridData as GridPaymentGridData)?.terms as (
                                    | number
                                    | null
                                )[];
                                const previousDecided = (currentOffer?.paymentGridData as GridPaymentGridData)?.decidedColumns as
                                    | Record<number, string>
                                    | undefined;
                                const dealColumnIndex = dealColumnIndexOf(currentOffer);
                                const landing = Object.fromEntries(
                                    Object.entries(projected).filter(([column]) => {
                                        const columnIndex = Number(column);
                                        return (
                                            !isHeldByMoreThanOneColumn(gridTerms, gridTerms[columnIndex]) ||
                                            columnIndex === dealColumnIndex ||
                                            previousDecided?.[columnIndex] == null
                                        );
                                    })
                                );
                                return { ...previousDecided, ...landing };
                            })(),
                            ...(nextRefusedTerms && { refusedTerms: nextRefusedTerms }),
                            ...(nextPairRejectedTerms && { pairRejectedTerms: nextPairRejectedTerms })
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_ORIGINAL_COLUMN_SOURCES: {
            const { offerType: actionOfferType, remember, rememberPrograms, replace, forget } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const gridData = currentOffer?.paymentGridData as GridPaymentGridData;
            const nextSources: Record<number, string> = { ...gridData?.originalColumnSources };
            const nextPrograms: Record<number, GenericObject> = { ...gridData?.originalColumnPrograms };

            Object.keys(remember ?? {}).forEach((key) => {
                const termMonths = Number(key);
                if (nextSources[termMonths] !== undefined) return;
                nextSources[termMonths] = remember[termMonths];
                if (rememberPrograms?.[termMonths]) nextPrograms[termMonths] = rememberPrograms[termMonths];
            });
            Object.keys(replace ?? {}).forEach((key) => {
                const termMonths = Number(key);
                nextSources[termMonths] = replace[termMonths];
                delete nextPrograms[termMonths];
            });
            ((forget ?? []) as number[]).forEach((termMonths) => {
                delete nextSources[termMonths];
                delete nextPrograms[termMonths];
            });

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            originalColumnSources: Object.keys(nextSources).length ? nextSources : undefined,
                            originalColumnPrograms: Object.keys(nextPrograms).length ? nextPrograms : undefined
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_CELL_PRICED_AT: {
            const { offerType: actionOfferType, cellPricedAt } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            // Merged, so re-pricing one row revises those cells' timestamps and leaves the
                            // rest at the time they actually priced.
                            cellPricedAt: {
                                ...(currentOffer?.paymentGridData as GridPaymentGridData)?.cellPricedAt,
                                ...cellPricedAt
                            }
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_PENDING_SELL_RATE: {
            const { offerType: actionOfferType, columnIndex, sellRate } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const previousPendingSellRates = (currentOffer?.paymentGridData as GridPaymentGridData)?.pendingSellRates;

            // An undefined rate removes that column's entry rather than storing a hole.
            const pendingSellRates = { ...previousPendingSellRates };
            if (sellRate == null) {
                delete pendingSellRates[columnIndex];
            } else {
                pendingSellRates[columnIndex] = sellRate;
            }

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            pendingSellRates
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_FALLBACK_RATES: {
            const { offerType: actionOfferType, ratesByTerm } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            // Merged, so one column's hold leaves the others alone. A `null` entry removes it - that column priced.
            const fallbackRatesByTerm = {
                ...(currentOffer?.paymentGridData as GridPaymentGridData)?.fallbackRatesByTerm
            };
            Object.keys(ratesByTerm).forEach((termMonths) => {
                if (ratesByTerm[termMonths] == null) delete fallbackRatesByTerm[termMonths];
                else fallbackRatesByTerm[termMonths] = ratesByTerm[termMonths];
            });

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            fallbackRatesByTerm
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_LEFT_TERMS: {
            const { offerType: actionOfferType, leftTermsByTerm: incoming } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            const leftTermsByTerm = {
                ...(currentOffer?.paymentGridData as GridPaymentGridData)?.leftTermsByTerm
            };
            Object.keys(incoming).forEach((termMonths) => {
                if (incoming[termMonths] == null) delete leftTermsByTerm[Number(termMonths)];
                else leftTermsByTerm[Number(termMonths)] = incoming[termMonths];
            });

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            leftTermsByTerm
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_BUILD: {
            const { offerType: actionOfferType, build } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            // Replaced, not merged - there is exactly one build in progress, and the previous one stops counting here.
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            build
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_DEAL_PROGRAM: {
            const { offerType: actionOfferType, dealProgram, calculatorPricedDealTerm } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            dealProgramAtOpen: dealProgram,
                            calculatorPricedDealTerm
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_DEAL_TERM_BEFORE_EDIT: {
            const { offerType: actionOfferType, termId, overrides } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            dealTermBeforeEdit: termId,
                            dealTermOverridesBeforeEdit: overrides
                        }
                    }
                }
            };
        }

        case types.HOLD_PAYMENT_GRID_FOR_VEHICLE_SWITCH:
        case types.REBUILD_PAYMENT_GRID_AFTER_VEHICLE_SWITCH: {
            const { offerType: actionOfferType } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            awaitingVehicleSwitch: action.type === types.HOLD_PAYMENT_GRID_FOR_VEHICLE_SWITCH || undefined
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_USER_PRICED_TERM: {
            const { offerType: actionOfferType, termMonths } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const previousUserPricedTerms = (currentOffer?.paymentGridData as GridPaymentGridData)?.userPricedTerms;

            // Merged per term, never replaced - a second column being priced must not forget the first.
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            userPricedTerms: { ...previousUserPricedTerms, [termMonths]: true }
                        }
                    }
                }
            };
        }

        case types.RESTORE_PAYMENT_GRID_TERM_OVERRIDES: {
            const { offerType: actionOfferType, byTerm } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const previousOverridesByTerm: Record<number, any> = (currentOffer as any)?.overridesByTerm || {};

            const nextOverridesByTerm = Object.keys(byTerm).reduce<Record<number, any>>(
                (accumulated, key) => {
                    const termMonths = Number(key);
                    accumulated[termMonths] = { ...accumulated[termMonths], ...byTerm[termMonths] };
                    return accumulated;
                },
                { ...previousOverridesByTerm }
            );

            const previousGridWrittenTerms = (currentOffer?.paymentGridData as GridPaymentGridData)?.gridWrittenTerms;
            const nextGridWrittenTerms = Object.keys(byTerm).reduce<Record<number, GridWrittenHalves>>(
                (accumulated, key) => {
                    const termMonths = Number(key);
                    accumulated[termMonths] = {
                        ...accumulated[termMonths],
                        ...(byTerm[termMonths].sellRate != null && { rate: true }),
                        ...(byTerm[termMonths].residualInfo != null && { residual: true })
                    };
                    return accumulated;
                },
                { ...previousGridWrittenTerms }
            );

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        overridesByTerm: nextOverridesByTerm,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            gridWrittenTerms: nextGridWrittenTerms
                        }
                    }
                }
            };
        }

        case types.CLEAR_PAYMENT_GRID_COLUMN_FIGURES: {
            const { offerType: actionOfferType, termMonths } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const previousOverridesByTerm: Record<number, any> = (currentOffer as any)?.overridesByTerm || {};
            const previousGridWrittenTerms = (currentOffer?.paymentGridData as GridPaymentGridData)?.gridWrittenTerms;

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        overridesByTerm: withoutGridWrittenOverrides(previousOverridesByTerm, {
                            [termMonths]: { rate: true, residual: true }
                        }),
                        ...(previousGridWrittenTerms?.[termMonths] && {
                            paymentGridData: {
                                ...currentOffer.paymentGridData,
                                gridWrittenTerms: Object.fromEntries(
                                    Object.entries(previousGridWrittenTerms).filter(([term]) => Number(term) !== termMonths)
                                )
                            }
                        })
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_WRITTEN_TERM: {
            const { offerType: actionOfferType, termMonths, half, clear } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            if (!currentOffer) return state;
            const previousGridWrittenTerms = (currentOffer.paymentGridData as any)?.gridWrittenTerms;

            const previousHalves = previousGridWrittenTerms?.[termMonths];
            const nextHalves = clear ? { ...previousHalves, [half]: undefined } : { ...previousHalves, [half]: true };
            const keptHalves = Object.fromEntries(Object.entries(nextHalves).filter(([, isWritten]) => isWritten));

            const nextGridWrittenTerms = { ...previousGridWrittenTerms };
            if (Object.keys(keptHalves).length) {
                nextGridWrittenTerms[termMonths] = keptHalves;
            } else {
                delete nextGridWrittenTerms[termMonths];
            }

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer.paymentGridData,
                            gridWrittenTerms: nextGridWrittenTerms
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_COLUMN_CELLS: {
            const { offerType: actionOfferType, cells } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            columnCells: mergeColumnCells(
                                (currentOffer?.paymentGridData as GridPaymentGridData)?.columnCells,
                                cells
                            )
                        }
                    }
                }
            };
        }

        case types.RESTORE_PAYMENT_GRID_ROW_QUOTES: {
            const { offerType: actionOfferType, quotedTermsByRow } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const previousRowsStatuses =
                ((currentOffer?.paymentGridData as GridPaymentGridData)?.rowsStatuses as Record<number, any> | undefined) ?? {};

            // Only `quotedTerms` is seeded: a restored row has no call in flight, so it claims no loading state.
            const rowsStatuses = Object.entries(quotedTermsByRow as Record<string, number[]>).reduce<Record<number, any>>(
                (statuses, [rowIndex, quotedTerms]) => {
                    const row = Number(rowIndex);
                    const previous = statuses[row];
                    return {
                        ...statuses,
                        [row]: {
                            ...previous,
                            quotedTerms: [...new Set([...(previous?.quotedTerms ?? []), ...quotedTerms])]
                        }
                    };
                },
                { ...previousRowsStatuses }
            );

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            rowsStatuses
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_COLUMN_OVERRIDE: {
            const { offerType: actionOfferType, columnIndex, override } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const previousColumnOverrides = (currentOffer?.paymentGridData as GridPaymentGridData)?.columnOverrides;
            const previousRestoredInputs = (currentOffer?.paymentGridData as GridPaymentGridData)?.restoredInputs;
            const previousCarriedOverrides = (currentOffer?.paymentGridData as GridPaymentGridData)?.carriedOverrides;

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            ...(previousRestoredInputs && {
                                restoredInputs: withoutHalvesWritten(previousRestoredInputs, columnIndex, override)
                            }),
                            ...(previousCarriedOverrides && {
                                carriedOverrides: withoutHalvesWritten(previousCarriedOverrides, columnIndex, override)
                            }),
                            columnOverrides: {
                                ...previousColumnOverrides,
                                [columnIndex]: {
                                    ...previousColumnOverrides?.[columnIndex],
                                    ...override,
                                    term: (currentOffer?.paymentGridData as GridPaymentGridData)?.terms?.[columnIndex]
                                }
                            }
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_RESTORED_INPUTS: {
            const { offerType: actionOfferType, restoredInputs } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            restoredInputs
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_CARRIED_OVERRIDE: {
            const { offerType: actionOfferType, columnIndex, carriedOverride } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const previousCarriedOverrides = (currentOffer?.paymentGridData as GridPaymentGridData)?.carriedOverrides;
            if (!carriedOverride && !previousCarriedOverrides?.[columnIndex]) return state;
            const { [columnIndex]: _replaced, ...others } = previousCarriedOverrides ?? {};

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            carriedOverrides: carriedOverride ? { ...others, [columnIndex]: carriedOverride } : others
                        }
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_DECIDED_COLUMNS: {
            const { offerType: actionOfferType, decidedColumns } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...currentOffer?.paymentGridData,
                            decidedColumns: {
                                ...(currentOffer?.paymentGridData as GridPaymentGridData)?.decidedColumns,
                                ...decidedColumns
                            }
                        }
                    }
                }
            };
        }

        case types.ADOPT_PAYMENT_GRID_CELL: {
            const {
                offerType: actionOfferType,
                cell,
                columnResidualInfo,
                columnRate,
                columnIndex: adoptedColumnIndex,
                termId
            } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const adoptedTermMonths = parseTermId(termId)?.termIdParams?.term;
            const gridWroteResidual =
                adoptedTermMonths != null &&
                (currentOffer?.paymentGridData as GridPaymentGridData)?.gridWrittenTerms?.[Number(adoptedTermMonths)]?.residual ===
                    true;
            const residualPair =
                columnResidualInfo || gridWroteResidual
                    ? {
                          programResidualOverride: columnResidualInfo?.programResidual,
                          programResidualPercentOverride: columnResidualInfo?.programResidualPercent
                      }
                    : {};

            const adoptedTermKey = adoptedTermMonths == null ? undefined : Number(adoptedTermMonths);
            const adoptedGridData = currentOffer?.paymentGridData as GridPaymentGridData | undefined;
            const previousTermOverride =
                adoptedTermKey == null ? undefined : (currentOffer as any)?.overridesByTerm?.[adoptedTermKey];
            const isTermHalfHeldForAnotherColumn = (key: 'sellRate' | 'residualInfo', half: 'rate' | 'residual') =>
                adoptedGridData?.gridWrittenTerms?.[adoptedTermKey as number]?.[half] === true ||
                (adoptedGridData?.terms ?? []).some((gridTerm, columnIndex) => {
                    const entry = adoptedGridData?.columnOverrides?.[columnIndex];
                    return (
                        columnIndex !== adoptedColumnIndex &&
                        gridTerm === adoptedTermKey &&
                        appliesToTerm(entry, adoptedTermKey) &&
                        entry[key] != null
                    );
                });
            const termHalfChange = (
                key: 'sellRate' | 'residualInfo',
                half: 'rate' | 'residual',
                ownValue: unknown,
                releasedKeys: string[]
            ): Record<string, unknown> => {
                if (ownValue != null) return { [key]: ownValue };
                if (previousTermOverride?.[key] == null || !isTermHalfHeldForAnotherColumn(key, half)) return {};
                return Object.fromEntries(releasedKeys.map((releasedKey): [string, undefined] => [releasedKey, undefined]));
            };
            const isAdoptedTermShared =
                adoptedTermKey != null &&
                (adoptedGridData?.terms ?? []).filter((gridTerm) => gridTerm === adoptedTermKey).length > 1;
            const adoptedRateType = isColumnRateType(columnRate?.rateType) ? columnRate?.rateType : undefined;
            const isLeaseAdoption = actionOfferType === LEASE;
            const rateChange = columnRate
                ? {
                      ...termHalfChange('sellRate', 'rate', columnRate.sellRate, ['sellRate', 'rateType']),
                      ...(columnRate.sellRate != null &&
                          (isLeaseAdoption || adoptedRateType) && {
                              rateType: adoptedRateType,
                              ...(isLeaseAdoption &&
                                  isRateInAnotherUnit(adoptedRateType, previousTermOverride?.buyRate) && { buyRate: undefined })
                          })
                  }
                : {};
            const residualChange = isAdoptedTermShared
                ? termHalfChange('residualInfo', 'residual', columnResidualInfo, ['residualInfo'])
                : {};
            const termOverrideChange = { ...rateChange, ...residualChange };
            const isRateReleased = 'sellRate' in rateChange && rateChange.sellRate == null;
            const dealLevelSellRate = isRateReleased ? cell?.sellRate : columnRate?.sellRate;
            const termOverridePair =
                adoptedTermKey != null && Object.keys(termOverrideChange).length
                    ? {
                          overridesByTerm: {
                              ...(currentOffer as any)?.overridesByTerm,
                              [adoptedTermKey]: { ...previousTermOverride, ...termOverrideChange }
                          },
                          ...(isUserProgramQuotes && dealLevelSellRate != null && { sellRateOverride: dealLevelSellRate })
                      }
                    : {};

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        ...residualPair,
                        ...termOverridePair
                    }
                }
            };
        }

        case types.SET_PAYMENT_GRID_ACTIVE_CELL: {
            const { offerType: actionOfferType, axis, index } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[actionOfferType as keyof typeof state.byOfferType];
            const gridData = currentOffer?.paymentGridData as GridPaymentGridData;
            const isColumn = axis === 'column';
            const axisValues = isColumn ? gridData?.terms : gridData?.cashDown;

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [actionOfferType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...gridData,
                            activeCell: {
                                ...gridData?.activeCell,
                                [isColumn ? 'columnIndex' : 'rowIndex']: index,
                                [isColumn ? 'term' : 'downPayment']: index == null ? undefined : axisValues?.[index]
                            }
                        }
                    }
                }
            };
        }

        case types.RESET_PAYMENT_GRID: {
            const { offerType, dropOverrides } = action.payload;
            if (!state.byOfferType) return state;
            const currentOffer = state.byOfferType?.[offerType as keyof typeof state.byOfferType];
            const paymentGridData = currentOffer.paymentGridData;
            const dealTermMonths = parseTermId((currentOffer as any)?.term)?.termIdParams?.term;
            const gridTerms = (paymentGridData as GridPaymentGridData)?.terms;
            const activeColumnIndex = (paymentGridData as GridPaymentGridData)?.activeCell?.columnIndex;
            const dealColumnIndex =
                activeColumnIndex != null && gridTerms?.[activeColumnIndex] === dealTermMonths
                    ? activeColumnIndex
                    : gridTerms?.indexOf(dealTermMonths as number);
            const dealColumnOverride =
                dealColumnIndex == null ? undefined : (paymentGridData as GridPaymentGridData)?.columnOverrides?.[dealColumnIndex];
            const writtenTerms = (paymentGridData as GridPaymentGridData)?.gridWrittenTerms;
            const dealTermHalves = dealTermMonths == null ? undefined : writtenTerms?.[dealTermMonths];
            const ledgerTermsAtClose =
                dealTermHalves && appliesToTerm(dealColumnOverride, dealTermMonths)
                    ? {
                          ...writtenTerms,
                          [dealTermMonths as number]: {
                              ...(dealTermHalves.rate && dealColumnOverride.sellRate == null && { rate: true as const }),
                              ...(dealTermHalves.residual && dealColumnOverride.residualInfo == null && { residual: true as const })
                          }
                      }
                    : writtenTerms;
            const typedHalves =
                dropOverrides === GRID_TYPED_OVERRIDES
                    ? overridesTypedInGrid(paymentGridData as GridPaymentGridData, dealTermMonths)
                    : {};
            const takenBackTypedTerms = Object.keys(typedHalves).map(Number);
            const gridWrittenTerms = takenBackTypedTerms.length
                ? {
                      ...ledgerTermsAtClose,
                      ...Object.fromEntries(
                          takenBackTypedTerms.map((termMonths): [number, GridWrittenHalves] => [
                              termMonths,
                              { ...ledgerTermsAtClose?.[termMonths], ...typedHalves[termMonths] }
                          ])
                      )
                  }
                : ledgerTermsAtClose;
            const collapsedOverridesByTerm = collapseColumnOverridesIntoTerms(
                (currentOffer as any)?.overridesByTerm,
                (paymentGridData as GridPaymentGridData)?.terms,
                (paymentGridData as GridPaymentGridData)?.columnOverrides,
                (paymentGridData as GridPaymentGridData)?.activeCell?.columnIndex,
                gridWrittenTerms
            );

            const previousOverridesByTerm: Record<number, any> = (currentOffer as any)?.overridesByTerm || {};

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...currentOffer,
                        paymentGridData: {
                            ...paymentGridData,
                            terms: undefined,
                            cashDown: undefined,
                            rowsStatuses: undefined,
                            columnSources: undefined,
                            originalColumnSources: undefined,
                            originalColumnPrograms: undefined,
                            refusedTerms: undefined,
                            pairRejectedTerms: undefined,
                            pendingSellRates: undefined,
                            fallbackRatesByTerm: undefined,
                            leftTermsByTerm: undefined,
                            userPricedTerms: undefined,
                            userEnteredTerms: undefined,
                            userEnteredColumns: undefined,
                            restoredInputs: undefined,
                            carriedOverrides: undefined,
                            splitOffTerms: undefined,
                            gridWrittenTerms: undefined,
                            build: undefined,
                            dealProgramAtOpen: undefined,
                            calculatorPricedDealTerm: undefined,
                            dealTermBeforeEdit: undefined,
                            dealTermOverridesBeforeEdit: undefined,
                            cellPricedAt: undefined,
                            columnCells: undefined,
                            decidedColumns: undefined,
                            erroredColumns: undefined,
                            columnOverrides: undefined,
                            activeCell: undefined
                        },
                        ...(dropOverrides === true
                            ? { overridesByTerm: {} }
                            : (() => {
                                  const afterTakeback = gridWrittenTerms
                                      ? withoutGridWrittenOverrides(previousOverridesByTerm, gridWrittenTerms)
                                      : previousOverridesByTerm;
                                  if (!collapsedOverridesByTerm) return { overridesByTerm: afterTakeback };
                                  const foldedOntoSurvivors = Object.fromEntries(
                                      Object.entries(collapsedOverridesByTerm).filter(
                                          ([termMonths]) =>
                                              afterTakeback[Number(termMonths)] != null &&
                                              !takenBackTypedTerms.includes(Number(termMonths))
                                      )
                                  );
                                  return { overridesByTerm: { ...afterTakeback, ...foldedOntoSurvivors } };
                              })())
                    }
                }
            };
        }

        case types.TOGGLE_SHOULD_SHOW_PAYMENTS_GRID: {
            const toggleStatus = action.payload;

            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'paymentGridToggleSP', toggleStatus);
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.TOGGLE_SHOULD_SHOW_PAYMENTS_GRID_FOR_TEMPLATE: {
            const toggleStatus = action.payload;

            const inputs = updateInputWithValue(state.byOfferType?.[FINANCE], 'paymentGridToggleSP', toggleStatus);
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, FINANCE)
            };
        }

        case types.SET_SHOULD_SAVE_MULTI_SCENARIO_PAYMENT_GRID: {
            const toggleStatus = action.payload;

            const inputs = updateInputWithValue(state.byOfferType?.[offerType], 'saveMultiScenarioPaymentGrid', toggleStatus);
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.UPDATE_GRID_HEADERS_SNAPSHOT: {
            const gridData = action.payload.gridData;
            const offerType = action.payload.offerType;

            const inputs = updateInputWithValue(
                state.byOfferType?.[offerType as keyof typeof state.byOfferType],
                'gridHeadersData',
                gridData
            );
            return {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerType)
            };
        }

        case types.PAYMENT_REQUESTED: {
            const isGridAction = action.meta?.originalAction?.meta?.paymentGrid?.gridAction || false;
            const isFlexBuyPayment = action.meta?.originalAction?.meta?.paymentGrid?.isFlexBuyPayment || false;

            if (isGridAction && !isFlexBuyPayment) {
                const currentOffer = state.byOfferType?.[offerType];
                let paymentGridData = currentOffer?.paymentGridData || {};
                paymentGridData = updatePaymentGridLoadingStatuses(offerType, paymentGridData, action, true);
                if (!state.byOfferType) return state;
                return {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...currentOffer,
                            paymentGridData: {
                                ...paymentGridData,
                                hasError: false,
                                erroredColumns: nextErroredColumns(
                                    (currentOffer?.paymentGridData as GridPaymentGridData)?.erroredColumns,
                                    action.meta?.originalAction?.meta?.gridColumnIndices,
                                    false
                                )
                            }
                        }
                    }
                };
            }
            return state;
        }

        // A row inherits the abortSignal of the action that fanned it out, so a second fee edit aborts the previous
        // round. Only the loading flag is released - an abort carries no values, so the row keeps what it showed.
        case types.PAYMENT_GRID_ROW_SUPERSEDED:
        case types.PAYMENT_ABORTED: {
            const { gridAction = false, isFlexBuyPayment = false } = action.meta?.originalAction?.meta?.paymentGrid || {};
            const currentOffer = state.byOfferType?.[offerType];

            if (!gridAction || isFlexBuyPayment || !state.byOfferType || !currentOffer?.paymentGridData) return state;

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...currentOffer,
                        // No hasError: an aborted request was never an answer.
                        paymentGridData: updatePaymentGridLoadingStatuses(offerType, currentOffer.paymentGridData, action, false)
                    }
                }
            };
        }

        case types.PAYMENT_FAILURE: {
            const isGridAction = action.meta?.originalAction?.meta?.paymentGrid?.gridAction || false;
            const isFlexBuyPayment = action.meta?.originalAction?.meta?.paymentGrid?.isFlexBuyPayment || false;

            if (isGridAction && !isFlexBuyPayment) {
                const currentOffer = state.byOfferType?.[offerType];
                let paymentGridData = currentOffer?.paymentGridData || {};
                paymentGridData = updatePaymentGridLoadingStatuses(offerType, paymentGridData, action, false);
                if (!state.byOfferType) return state;
                return {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...currentOffer,
                            paymentGridData: {
                                ...paymentGridData,
                                hasError: true,
                                erroredColumns: nextErroredColumns(
                                    (currentOffer?.paymentGridData as GridPaymentGridData)?.erroredColumns,
                                    action.meta?.originalAction?.meta?.gridColumnIndices,
                                    true
                                )
                            }
                        }
                    }
                };
            }
            return state;
        }

        default: {
            return state;
        }
    }
};
